import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Settings } from 'lucide-react'
import { ExportButton } from '@/components/ui/ExportButton'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { apiErrorText } from '@/lib/api-error'
import { useTerminology } from '@/lib/useTerminology'
import { changeWorkStepStatus, releaseWorkSteps } from '../../api/projects-phase-d.api'
import { cockpitListExportRequest, fetchCockpitList, type CockpitFilter } from '../../api/work-cockpit.api'
import { WORK_STEP_STATUS_LABELS } from '../../project-labels'
import type { CockpitCard } from '../../types/cockpit.types'
import { formatStunden, kurzDatum, zeitraumKurz } from './cockpit-labels'

interface ColDef {
  key: string
  label: string
  align?: 'right'
  sort?: string
}

const COLUMN_LAYOUT_KEY = 'columnbar_layout_work_cockpit'
const DEFAULT_COLUMN_KEYS = ['project', 'position', 'status', 'planned', 'actual', 'due', 'assigned']

type Gruppierung = '' | 'project' | 'group' | 'user' | 'week'

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
}

function loadColumnState(): ColumnState {
  const gespeichert = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  if (gespeichert?.columnKeys?.length) return { columnKeys: gespeichert.columnKeys, columnWidths: gespeichert.columnWidths ?? {} }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {} }
}

/**
 * AP-35 Detailblatt 11 4.4 — **die Sicht Liste.**
 *
 * Listen-Standard (`docs/pattern-auswertungsmaske.md`): konfigurierbare Spalten,
 * Sortierung, Gruppierung, Export mit Mailweg, Hilfe-Popover. Die Filter stehen
 * über allen drei Sichten in der Seite — Board und Liste lesen dieselben.
 * Mehrfachauswahl mit denselben Aktionen wie das Board.
 */
export function CockpitList({
  phaseKey,
  phaseName,
  filter,
  filterInfo,
  darfEinteilen,
  darfFreigeben,
  darfStatus,
  version,
  onOpen,
  onEinteilen,
  onChanged,
}: {
  phaseKey: string
  phaseName: string
  filter: CockpitFilter
  filterInfo: string
  darfEinteilen: boolean
  darfFreigeben: boolean
  darfStatus: boolean
  version: number
  onOpen: (karte: CockpitCard, liste: CockpitCard[]) => void
  onEinteilen: (stepIds: string[]) => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const alleSpalten: ColDef[] = [
    { key: 'project', label: terms.project, sort: 'project' },
    { key: 'position', label: 'Position' },
    { key: 'status', label: 'Status', sort: 'status' },
    { key: 'release', label: 'Freigabe' },
    { key: 'planned', label: 'Soll h', align: 'right', sort: 'plannedHours' },
    { key: 'actual', label: 'Ist h', align: 'right' },
    { key: 'due', label: 'fällig', sort: 'due' },
    { key: 'fixed', label: 'fixiert' },
    { key: 'assigned', label: 'eingeteilt' },
    { key: 'material', label: 'Material' },
    { key: 'group', label: 'Gruppe' },
    { key: 'client', label: 'Kunde' },
  ]

  const [zeilen, setZeilen] = useState<CockpitCard[]>([])
  const [meta, setMeta] = useState<{ total: number; page: number; totalPages: number } | null>(null)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('due')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [gruppierung, setGruppierung] = useState<Gruppierung>('')
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const laden = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await fetchCockpitList(phaseKey, { ...filter, page, limit: 50, sortBy, sortDir })
      setZeilen(r.data)
      setMeta(r.meta)
    } catch (e) {
      setError(apiErrorText(e, 'Die Liste konnte nicht geladen werden.'))
    } finally {
      setLoading(false)
    }
  }, [phaseKey, filter, page, sortBy, sortDir])

  useEffect(() => {
    void laden()
  }, [laden, version])

  useEffect(() => setPage(1), [phaseKey, filter])

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  const shown = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))

  const renderCell = (k: CockpitCard, key: string): React.ReactNode => {
    switch (key) {
      case 'project':
        return `${k.project.projectNumber} ${k.project.name}`
      case 'position':
        return `${k.position.number} ${k.position.name}`
      case 'status': {
        const s = WORK_STEP_STATUS_LABELS[k.executionStatus]
        return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${s?.class ?? ''}`}>{s?.label ?? k.executionStatus}</span>
      }
      case 'release':
        return k.marks.notReleased ? `nicht freigegeben${k.releaseNote ? ` · ${k.releaseNote}` : ''}` : 'freigegeben'
      case 'planned':
        return formatStunden(k.plannedHoursTotal)
      case 'actual':
        return formatStunden(k.actualHours)
      case 'due':
        return <span className={k.overdue ? 'font-medium text-destructive' : undefined}>{kurzDatum(k.dueAt)}</span>
      case 'fixed':
        return k.fixedAt ? `${kurzDatum(k.fixedAt)} ${k.fixedTime ?? ''}` : '—'
      case 'assigned': {
        const laufend = k.assignments.filter((a) => a.current)
        return (
          <button type="button" className="text-left hover:underline disabled:no-underline" disabled={!darfEinteilen} onClick={() => onEinteilen([k.stepId])}>
            {laufend.length ? laufend.map((a) => `${a.userName} ${zeitraumKurz(a.startsOn, a.endsOn)}`).join(', ') : <span className="italic text-muted-foreground">nicht eingeteilt</span>}
          </button>
        )
      }
      case 'material':
        return k.marks.materialMissing ? 'fehlt' : ''
      case 'group':
        return k.groupName ?? ''
      case 'client':
        return k.project.clientName ?? ''
      default:
        return null
    }
  }

  const gruppenSchluessel = (k: CockpitCard): string => {
    switch (gruppierung) {
      case 'project':
        return `${k.project.projectNumber} ${k.project.name}`
      case 'group':
        return k.groupName ?? 'ohne Gruppe'
      case 'user':
        return k.assignments.find((a) => a.current)?.userName ?? 'nicht eingeteilt'
      case 'week': {
        if (!k.dueAt) return 'ohne Termin'
        const d = new Date(`${k.dueAt}T00:00:00Z`)
        const donnerstag = new Date(d.getTime() + (3 - ((d.getUTCDay() + 6) % 7)) * 86_400_000)
        const jahr = donnerstag.getUTCFullYear()
        const kw = Math.floor((donnerstag.getTime() - Date.UTC(jahr, 0, 1)) / 86_400_000 / 7) + 1
        return `KW ${kw}/${jahr}`
      }
      default:
        return ''
    }
  }

  const gruppiert: [string, CockpitCard[]][] = gruppierung
    ? Object.entries(
        zeilen.reduce<Record<string, CockpitCard[]>>((acc, k) => {
          const g = gruppenSchluessel(k)
          ;(acc[g] ??= []).push(k)
          return acc
        }, {}),
      )
    : [['', zeilen]]

  const gewaehlt = [...auswahl]
  const alleGewaehlt = zeilen.length > 0 && zeilen.every((z) => auswahl.has(z.stepId))
  const totalCols = 2 + shown.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0)

  async function aktion(f: () => Promise<unknown>) {
    try {
      await f()
      setAuswahl(new Set())
      onChanged()
    } catch (e) {
      setError(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          gruppieren
          <select className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground" value={gruppierung} onChange={(e) => setGruppierung(e.target.value as Gruppierung)}>
            <option value="">—</option>
            <option value="project">{terms.project}</option>
            <option value="group">Gruppe</option>
            <option value="user">Mitarbeiter</option>
            <option value="week">Woche der Fälligkeit</option>
          </select>
        </label>
        {auswahl.size > 0 && (
          <div className="flex items-center gap-2 rounded-md bg-primary/10 px-2 py-1 text-xs">
            <span>{auswahl.size} gewählt</span>
            {darfEinteilen && (
              <button type="button" className="rounded border border-border px-2 py-0.5 hover:bg-muted" onClick={() => onEinteilen(gewaehlt)}>
                einteilen
              </button>
            )}
            {darfFreigeben && (
              <button type="button" className="rounded border border-border px-2 py-0.5 hover:bg-muted" onClick={() => void aktion(() => releaseWorkSteps(gewaehlt))}>
                freigeben
              </button>
            )}
          </div>
        )}
        <div className="ml-auto">
          <ExportButton
            request={(format, orientation) =>
              cockpitListExportRequest(
                phaseKey,
                { ...filter, sortBy, sortDir, format, orientation, filterInfo },
                shown.map((c) => ({ key: c.key, label: c.label })),
              )
            }
            mail={{
              source: 'EXPORT_WORK_COCKPIT',
              title: t('mailExport.titles.EXPORT_WORK_COCKPIT'),
              subject: `${t('mailExport.titles.EXPORT_WORK_COCKPIT')} ${phaseName}`,
            }}
          />
        </div>
      </div>

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {} }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8 px-2">
                  <input
                    type="checkbox"
                    aria-label="alle wählen"
                    checked={alleGewaehlt}
                    onChange={(e) => setAuswahl(e.target.checked ? new Set(zeilen.map((z) => z.stepId)) : new Set())}
                  />
                </th>
                <th className="px-4 py-1.5 text-left font-medium text-foreground">
                  <button
                    type="button"
                    className="flex items-center gap-1"
                    onClick={() => {
                      setSortDir(sortBy === 'step' && sortDir === 'asc' ? 'desc' : 'asc')
                      setSortBy('step')
                    }}
                  >
                    Schritt {sortBy === 'step' ? sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </button>
                </th>
                {shown.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                      columnConfigMode ? 'cursor-grab' : ''
                    } ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={
                      columnConfigMode
                        ? (e) => {
                            e.preventDefault()
                            setDragOverKey(col.key)
                          }
                        : undefined
                    }
                    onDrop={
                      columnConfigMode
                        ? () => {
                            const quelle = dragKeyRef.current
                            setDragOverKey(null)
                            if (!quelle || quelle === col.key) return
                            updateColState((prev) => {
                              const keys = [...prev.columnKeys]
                              keys.splice(keys.indexOf(quelle), 1)
                              keys.splice(keys.indexOf(col.key), 0, quelle)
                              return { ...prev, columnKeys: keys }
                            })
                          }
                        : undefined
                    }
                    onDragEnd={() => setDragOverKey(null)}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {col.sort && !columnConfigMode ? (
                        <button
                          type="button"
                          className="flex items-center gap-1"
                          onClick={() => {
                            setSortDir(sortBy === col.sort && sortDir === 'asc' ? 'desc' : 'asc')
                            setSortBy(col.sort!)
                          }}
                        >
                          {col.label}
                          {sortBy === col.sort ? sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && zeilen.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-muted-foreground">
                    Laden…
                  </td>
                </tr>
              )}
              {!loading && zeilen.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-muted-foreground">
                    Keine Arbeitsschritte in dieser Phase.
                  </td>
                </tr>
              )}
              {gruppiert.map(([name, reihe]) => (
                <GruppenBlock key={name || '-'} name={name} spalten={totalCols}>
                  {reihe.map((k, idx) => (
                    <tr key={k.stepId} className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/30`}>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`${k.number} wählen`}
                          checked={auswahl.has(k.stepId)}
                          onChange={(e) =>
                            setAuswahl((a) => {
                              const n = new Set(a)
                              if (e.target.checked) n.add(k.stepId)
                              else n.delete(k.stepId)
                              return n
                            })
                          }
                        />
                      </td>
                      <td className="cursor-pointer whitespace-nowrap px-4 py-1.5" onClick={() => onOpen(k, zeilen)}>
                        <span className="font-mono text-muted-foreground">{k.number}</span> {k.activityName}
                        {darfStatus && ['READY', 'ASSIGNED', 'IN_PROGRESS'].includes(k.executionStatus) && (
                          <button
                            type="button"
                            className="ml-2 rounded border border-border px-1.5 text-xs hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation()
                              void aktion(() => changeWorkStepStatus(k.stepId, { to: 'DONE' }))
                            }}
                          >
                            fertig
                          </button>
                        )}
                      </td>
                      {shown.map((col) => (
                        <td key={col.key} className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                          {renderCell(k, col.key)}
                        </td>
                      ))}
                      {columnConfigMode && availableToAdd.length > 0 && <td />}
                    </tr>
                  ))}
                </GruppenBlock>
              ))}
            </tbody>
          </table>
        </div>

        {meta && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              {meta.total} Arbeitsschritte{meta.totalPages > 1 ? ` · Seite ${meta.page} von ${meta.totalPages}` : ''}
            </span>
            {meta.totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                  ‹
                </button>
                <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                  ›
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function GruppenBlock({ name, spalten, children }: { name: string; spalten: number; children: React.ReactNode }) {
  return (
    <>
      {name && (
        <tr className="bg-muted/60">
          <td colSpan={spalten} className="px-4 py-1 text-xs font-semibold text-muted-foreground">
            {name}
          </td>
        </tr>
      )}
      {children}
    </>
  )
}
